import { useCallback, useEffect, useState } from 'react'

import { AttachFileRounded, CloseRounded, WarningRounded } from '@mui/icons-material'
import { Box, Card, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { useSmallScreen } from '@common/utils'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {'local'|'remote'} [props.type]
 * @param {'horizontal'|'vertical'} [props.orientation]
 * @param {Object[]} props.attachments
 * @param {Function} [props.onRemove]
 * @param {Function} [props.onClick]
 * @param {number} [props.fileSizeLimitMb]
 */
export default function AttachmentList({
  type = 'local',
  orientation = 'horizontal',
  attachments,
  onRemove,
  onClick,
  fileSizeLimitMb = 5,
}) {
  const theme = useTheme()
  const isSmallScreen = useSmallScreen()
  const [thumbnails, setThumbnails] = useState({})

  const nameProp = type === 'local' ? 'name' : 'filename'
  const typeProp = type === 'local' ? 'type' : 'mimeType'

  const generateThumbnail = useCallback((file) => {
    if (type === 'local') {
      if (file.type.startsWith('image/')) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            resolve(e.target.result)
          }
          reader.onerror = () => {
            reject(new Error('File reading failed'))
          }
          reader.readAsDataURL(file)
        })
      }
      return null
    }
    if (type === 'remote') {
      if (file.mimeType.startsWith('image/')) return file.url
      return null
    }
    return null
  }, [])

  const generateThumbnails = useCallback(async (files) => {
    const generatedThumbnails = await Promise.all(files.map(generateThumbnail))
    const result = files.reduce(
      (acc, file, index) => ({ ...acc, [file[nameProp]]: generatedThumbnails[index] }),
      {},
    )
    setThumbnails(result)
  }, [])

  useEffect(() => {
    generateThumbnails(attachments)
  }, [attachments])

  return (
    <Box
      display="flex"
      flexDirection={orientation === 'horizontal' ? 'row' : 'column'}
      alignItems="center"
      gap={2}
      px={0.5}
      py={0.5}
      pt={1.5}
      sx={{ overflow: 'auto' }}
    >
      {attachments.map((file) => {
        const imageThumbnail = thumbnails[file[nameProp]]
        const isSizeExceedsLimit =
          type === 'local' ? file.size > fileSizeLimitMb * 1024 * 1024 : false

        return (
          <Card
            key={file.id ?? file[nameProp]}
            onClick={onClick ? () => onClick(file) : null}
            sx={{
              position: 'relative',
              flex: '0 0 auto',
              height: 170,
              width: 260,
              p: 1,
              overflow: 'visible',
              backgroundImage: imageThumbnail ? `url(${imageThumbnail})` : null,
              backgroundSize: 'cover',
              objectFit: 'scale-down',
              '&:hover > .remove-btn': { visibility: 'visible', opacity: 1 },
              cursor: onClick ? 'pointer' : 'auto',
            }}
          >
            <Box
              position="absolute"
              height="25%"
              width={1}
              sx={{
                backgroundImage:
                  'linear-gradient(to bottom, transparent,rgba(0, 0, 0, 0.4))',
              }}
              left={0}
              bottom={0}
            />
            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              alignItems="center"
              justifyContent="center"
              height={1}
            >
              {!imageThumbnail && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <AttachFileRounded sx={{ color: 'grey' }} />
                  <Typography
                    variant="caption"
                    color="grey"
                    sx={{ userSelect: 'none' }}
                  >
                    {file[typeProp]}
                  </Typography>
                </Box>
              )}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="start"
                position="absolute"
                bottom={0}
                left={isSizeExceedsLimit ? 0 : 6}
                width={1}
                color="white"
                fontSize={12}
                gap={0.5}
                sx={{ textShadow: '1px 1px 3px black', pr: 1 }}
              >
                {isSizeExceedsLimit && (
                  <Tooltip title={`File size exceeds ${fileSizeLimitMb} MB`}>
                    <WarningRounded sx={{ color: 'error.main' }} />
                  </Tooltip>
                )}
                <Tooltip title={file[nameProp]} enterDelay={500} enterNextDelay={500}>
                  <Typography
                    variant="caption"
                    color="white"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    sx={{ userSelect: 'none' }}
                  >
                    {file[nameProp]}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
            {onRemove && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                className="remove-btn"
                onClick={(event) => {
                  event.stopPropagation()
                  onRemove(file)
                }}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 20,
                  height: 20,
                  backgroundColor: 'error.main',
                  borderRadius: '40px',
                  cursor: 'pointer',
                  visibility: isSmallScreen.mobile ? 'visible' : 'hidden',
                  opacity: isSmallScreen.mobile ? 1 : 0,
                  transition: theme.transitions.create('opacity', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                }}
              >
                <CloseRounded sx={{ color: 'white', fontSize: 14 }} />
              </Box>
            )}
          </Card>
        )
      })}
    </Box>
  )
}
